/**
 * Loaders/types for the read-only config the pipeline publishes under
 * data/config/ (synced to public/data/config/ by scripts/sync-data.mjs).
 * This module never writes back to that data — viewer-builder's write
 * scope excludes data/config/ entirely (owned by the orchestrator).
 */
import { LOCAL_ORIGIN_EASTING, LOCAL_ORIGIN_NORTHING } from './coordinates';

export interface ManifestTile {
  id: string;
  lod: string;
  gridref_1km: string;
  /** [min easting, min northing, max easting, max northing] */
  bounds_bng: [number, number, number, number];
  crs: string;
  error_budget_m: number;
  achieved_max_error_m: number;
  vertex_count: number;
  triangle_count: number;
  file: string;
  sha256: string;
  infill: boolean;
}

export interface DataManifest {
  crs: string;
  local_origin: [number, number];
  coordinate_convention: string;
  tiles: ManifestTile[];
}

export interface Viewpoint {
  id: string;
  name: string;
  easting: number | null;
  northing: number | null;
  eye_height_m?: number;
  bearing_deg?: number;
  fov_deg?: number;
  observer_elevation_m_aod_user_supplied?: number;
  coordinate_status: string;
  /** Free-text provenance for an estimated observer coordinate (issue #10). */
  coordinate_source?: string;
}

export interface ViewpointsFile {
  crs: string;
  viewpoints: Viewpoint[];
}

export interface TurbineScenario {
  id: string;
  label: string;
  /**
   * Provenance flag distinguishing the real, operational Ray I farm
   * (`"osm_asbuilt"`) from every size-variant/proposed scenario (Ray II
   * scoping, and the Ray One turbine-size stand-in reusing Ray II's
   * positions). Drives both the turbine-size dropdown filter and the
   * turbine material colour-coding (issue #43) — see
   * `splitTurbineScenarios`/`turbineMaterialVariant` in scene/turbines.ts.
   */
  coordinate_status: string;
  hub_height_m: number;
  tip_height_m: number;
  rotor_radius_m: number;
  rotor_diameter_m: number;
  /** Free-text provenance for this scenario's envelope (issue #179's
   * "assessment basis" readout — turbine geometry is disclosed with where it
   * came from, never presented as bare, unsourced numbers). Optional: a
   * scenario missing this reads "source not stated" rather than a
   * fabricated citation (hard constraint #7). */
  source?: string;
  turbines: Array<{ id: string; easting: number; northing: number; ground_elev_m_aod: number }>;
}

export interface TurbineScenariosFile {
  schema_version: string;
  crs: string;
  units: string;
  scenarios: TurbineScenario[];
}

export interface Building {
  id: string;
  name?: string;
  levels?: number;
  height_m?: number | null;
  footprint_bng: [number, number][];
}

export interface BuildingsFile {
  crs: string;
  source: string;
  buildings: Building[];
}

/**
 * Shape of data/config/appearance_ground_truth.json (issue #146/#157):
 * hand-checked appearance labels keyed by building id, used only to gate the
 * barn open-front/open-sided void-face rendering (scene/barnVerification.ts,
 * issue #147) against classifier accuracy. Declared here (core, alongside
 * BuildingsFile) rather than in scene/, which depends on core/ and not the
 * other way around.
 */
export type AppearanceGroundTruth = Record<
  string,
  {
    fields?: {
      barn_form?: 'enclosed' | 'open_front' | 'open_sided' | null;
    };
  }
>;

/** A named-place orientation beacon (issue #4). Approximate, OS/OSM-sourced,
 * visual/orientation only — never an LOS input. */
export interface Location {
  id: string;
  name: string;
  /** Optional short context shown after the name, e.g. "village on the A68". */
  context?: string;
  easting: number;
  northing: number;
}

export interface LocationsFile {
  crs: string;
  locations: Location[];
}

/** Off-map radar / aviation facility (issue #171). Scene furniture only — never
 * an LOS input. Coordinates are published site positions from facilities.json. */
export interface Facility {
  id: string;
  name: string;
  kind: 'mod_radar' | 'civil_aviation';
  easting: number;
  northing: number;
  coordinate_provenance?: string;
}

export interface FacilitiesFile {
  crs: string;
  coordinate_provenance_note?: string;
  facilities: Facility[];
}

/** A polygon waterbody (lough / pond / reservoir) — OSM area, illustrative
 * scene furniture only, never an LOS input (issue #25). */
export interface Waterbody {
  id: string;
  name?: string;
  kind: 'water' | 'reservoir' | 'riverbank';
  /** OPEN BNG ring (first point != last). */
  polygon_bng: [number, number][];
}

/** A line waterway (river / burn) — OSM line, illustrative furniture only. */
export interface Waterway {
  id: string;
  name?: string;
  kind: 'river' | 'stream';
  /** OPEN BNG polyline. */
  polyline_bng: [number, number][];
}

export interface WaterFile {
  crs: string;
  source: string;
  waterbodies: Waterbody[];
  waterways: Waterway[];
}

/** A tarmac/classified road (OSM line). Carrier for the roadside dry-stone
 * walls (issue #22) — illustrative scene furniture only, never an LOS input. */
export interface Road {
  id: string;
  name?: string;
  /** OSM route number tag (e.g. "A696"), when the way carries one (issue #24). */
  ref?: string;
  road_class: 'primary' | 'secondary' | 'tertiary' | 'unclassified' | 'service' | 'trunk';
  surface?: string;
  /** OPEN BNG polyline. */
  polyline_bng: [number, number][];
}

export interface RoadsFile {
  crs: string;
  source: string;
  roads: Road[];
}

/** One densified centreline vertex of a build-time-draped road (issue #141):
 * `[easting, northing, height]` where height is metres AOD sampled from the
 * LOD1 TIN, or `null` where the vertex has no covering tile (never invented —
 * hard constraint #6; the viewer skips any ribbon segment touching a null). */
export type DrapedRoadVertex = [number, number, number | null];

/** A road with its ground heights baked at build time by
 * scripts/10_bake_road_drape.py, so the viewer builds the finished ribbon mesh
 * with no runtime ground sampling. Self-contained: carries its own densified
 * polyline, so the viewer never re-densifies or cross-references roads.json. */
export interface DrapedRoad {
  id: string;
  road_class: Road['road_class'];
  name?: string;
  ref?: string;
  draped_polyline: DrapedRoadVertex[];
}

export interface RoadsDrapedFile {
  crs: string;
  source: string;
  /** Densification spacing (m) the bake used — informational; the viewer
   * consumes the baked vertices directly rather than re-densifying. */
  densify_spacing_m: number;
  roads: DrapedRoad[];
}

/** OSM landcover class (issue #56). Area classes (forest/wood/scrub) carry a
 * CLOSED ring; tree_row carries an OPEN polyline. */
export type LandcoverClass = 'forest' | 'wood' | 'scrub' | 'tree_row';

/** One OSM landcover feature — illustrative scene furniture only, never an LOS
 * input (issue #56). `geometry` is an array of BNG [easting, northing] points:
 * a CLOSED ring for forest/wood/scrub, an OPEN polyline for tree_row. */
export interface LandcoverFeature {
  id: string;
  landcover_class: LandcoverClass;
  geometry: [number, number][];
}

export interface LandcoverFile {
  crs: string;
  source: string;
  features: LandcoverFeature[];
}

export class ManifestOriginMismatchError extends Error {
  constructor(manifestOrigin: [number, number]) {
    super(
      `data_manifest.json local_origin [${manifestOrigin.join(', ')}] does not match the ` +
        `viewer's hardcoded local origin [${LOCAL_ORIGIN_EASTING}, ${LOCAL_ORIGIN_NORTHING}]. ` +
        'Refusing to proceed rather than silently misplacing every tile (hard constraint #4).',
    );
    this.name = 'ManifestOriginMismatchError';
  }
}

/** Fetches and validates data_manifest.json. Throws if the origin disagrees with this viewer's constants. */
export async function loadManifest(url = '/data/config/data_manifest.json'): Promise<DataManifest> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch manifest at ${url}: ${res.status} ${res.statusText}`);
  }
  const manifest = (await res.json()) as DataManifest;
  assertManifestOrigin(manifest);
  return manifest;
}

export function assertManifestOrigin(manifest: Pick<DataManifest, 'local_origin'>): void {
  const [e, n] = manifest.local_origin;
  if (e !== LOCAL_ORIGIN_EASTING || n !== LOCAL_ORIGIN_NORTHING) {
    throw new ManifestOriginMismatchError(manifest.local_origin);
  }
}

export async function loadViewpoints(url = '/data/config/viewpoints.json'): Promise<ViewpointsFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch viewpoints at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as ViewpointsFile;
}

export async function loadTurbineScenarios(
  url = '/data/config/turbine_scenarios_resolved.json',
): Promise<TurbineScenariosFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch turbine scenarios at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as TurbineScenariosFile;
}

export async function loadBuildings(url = '/data/config/buildings.json'): Promise<BuildingsFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch buildings at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as BuildingsFile;
}

/**
 * Hand-checked appearance labels (issue #146/#157) that gate the barn
 * open-front/open-sided void-face rendering (issue #147) -- see
 * scene/barnVerification.ts. Unlike loadBuildings, a missing/unreachable file
 * is NOT a boot blocker: it degrades to an empty ground-truth set, which is
 * itself a legitimate outcome (every barn renders enclosed, exactly like
 * before this feature existed) rather than a fabricated one (hard constraint
 * #7 is about not inventing data, not about treating an optional QA artefact
 * as load-bearing).
 */
export async function loadAppearanceGroundTruth(
  url = '/data/config/appearance_ground_truth.json',
): Promise<AppearanceGroundTruth> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch appearance ground truth at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as AppearanceGroundTruth;
}

export async function loadWater(url = '/data/config/water.json'): Promise<WaterFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch water at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as WaterFile;
}

export async function loadRoads(url = '/data/config/roads.json'): Promise<RoadsFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch roads at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as RoadsFile;
}

export async function loadRoadsDraped(url = '/data/config/roads_draped.json'): Promise<RoadsDrapedFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch draped roads at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as RoadsDrapedFile;
}

export async function loadLandcover(url = '/data/config/landcover.json'): Promise<LandcoverFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch landcover at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as LandcoverFile;
}

export async function loadLocations(url = '/data/config/locations.json'): Promise<LocationsFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch locations at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as LocationsFile;
}

export async function loadFacilities(url = '/data/config/facilities.json'): Promise<FacilitiesFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch facilities at ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as FacilitiesFile;
}

/** Tile centre in local viewer-space (x, z), given its BNG bounds. */
export function tileLocalCentre(tile: Pick<ManifestTile, 'bounds_bng'>): { x: number; z: number } {
  const [minE, minN, maxE, maxN] = tile.bounds_bng;
  return {
    x: (minE + maxE) / 2 - LOCAL_ORIGIN_EASTING,
    z: LOCAL_ORIGIN_NORTHING - (minN + maxN) / 2, // north = -z (see coordinates.ts)
  };
}

/**
 * Tile axis-aligned bounds in local viewer-space (x, z), given its BNG bounds.
 * north = -z (see coordinates.ts), so BNG maxN maps to the minimum z and BNG
 * minN to the maximum z — the z bounds are the flip of the northing bounds.
 * Used to place a tile into the collider's grid-cell index (issue #80).
 */
export function tileLocalBounds(tile: Pick<ManifestTile, 'bounds_bng'>): {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
} {
  const [minE, minN, maxE, maxN] = tile.bounds_bng;
  return {
    minX: minE - LOCAL_ORIGIN_EASTING,
    maxX: maxE - LOCAL_ORIGIN_EASTING,
    minZ: LOCAL_ORIGIN_NORTHING - maxN, // north = -z: maxN -> min z
    maxZ: LOCAL_ORIGIN_NORTHING - minN, // north = -z: minN -> max z
  };
}
